import React, { useEffect, useState } from "react";
import { Row, Spinner } from "react-bootstrap";
import { InputPassword } from "../../components/inputs/password";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik";
import { profileSchema } from "./validation/profile";
import { useCountriesQuery } from "../../graphql/country.hooks";
import { useTranslation } from "react-i18next";
import { CustomToast } from "../../components/inputs/toast";
import { ConfirmModal } from "../../components/modals/confirmModal";
import {
  useEditProfileMutation,
  useGeoDataQuery,
  useProfileDeleteMutation,
} from "../../graphql/profile.hooks";
import { StyledInput } from "../../components/inputs/styledInput";
import ReactSelect from "../../components/inputs/select";
import { SelectOption } from "../../types";
import { InfoCircle } from "react-bootstrap-icons";
import { useUserQuery } from "../../graphql/menu.hooks";

export const Profile = () => {
  const { t } = useTranslation();

  const [showToast, setShowToast] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [countriesOptions, setCountriesOptions] = useState<SelectOption[]>([]);
  const [country, setCountry] = useState<string>("");

  const { data: countriesData, loading: countriesLading } = useCountriesQuery();
  const { data: userData, loading: userLoading } = useUserQuery();
  const { data: geoData } = useGeoDataQuery();

  const [editProfile, { loading: editLoading }] = useEditProfileMutation();
  const [deleteProfile, { loading: deleteLoading }] =
    useProfileDeleteMutation();

  useEffect(() => {
    if (!countriesData?.countries) return;
    setCountriesOptions(
      countriesData?.countries.map((item) => ({
        value: item.id,
        label: item.title,
      }))
    );
  }, [countriesData?.countries]);

  useEffect(() => {
    const countryIsoCode = geoData?.geoData.countryIsoCode;

    if (!userData?.user?.phone && countryIsoCode) {
      setCountry(countryIsoCode.toLocaleLowerCase());
    }
  }, [geoData, userData]);

  useEffect(() => {
    document.title = "Profile";
  }, []);

  const initialValues = {
    firstName: userData?.user.firstName || "",
    lastName: userData?.user.lastName || "",
    phone: userData?.user.phone || "",
    email: userData?.user.email || "",
    countryId: userData?.user?.country?.id || "",
    companyName: userData?.user?.userCompany?.companyName || "",
    companyEmail: userData?.user?.userCompany?.companyEmail || "",
    companyWebsite: userData?.user?.userCompany?.companyWebsite || "",
    companyTaxVat: userData?.user?.userCompany?.companyTaxVat || "",
    companyAddress: userData?.user?.userCompany?.companyAddress || "",
    password: "",
    passwordConfirmed: "",
  };

  const trySaveChanges = async (values: any) => {
    if (values.password !== values.passwordConfirmed) return;
    const user = Object.entries(values)
      .filter(([, value]) => value)
      .reduce((acc: any, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    user.id = userData.user.id;
    user.countryId = +user.countryId;
    user.companyName = user?.companyName || "";
    user.companyEmail = user?.companyEmail || "";
    user.companyWebsite = user?.companyWebsite || "";
    user.companyTaxVat = user?.companyTaxVat || "";
    user.companyAddress = user?.companyAddress || "";

    try {
      await editProfile({ variables: user });
      setShowToast(true);
    } catch (err) {
      console.error(err);
    }
  };

  const tryDeleteAccount = async () => {
    setShowConfirmModal(false);
    try {
      const { data } = await deleteProfile({
        variables: { id: userData.user.id },
      });
      if (data.profileDelete) {
        window.location.href = process.env.REACT_APP_ME_PAGE_URL;
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (userLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("profile.loading")}...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4 mb-md-5">
        <div>
          <h1 className="mb-0">{t("menu.profile")}</h1>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={profileSchema(t)}
        onSubmit={trySaveChanges}
      >
        {({ setFieldValue, handleSubmit, touched, errors, values }) => (
          <Form className="g-4 py-4 py-md-5 d-flex justify-content-center align-items-center bg-white rounded position-relative">
            <div className="col-11 col-md-9">
              <Row className="mb-4 mb-md-5">
                <p className="fw-bold fs-3">{t("profile.information")}</p>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="firstName"
                    className="fs-7"
                    label={t("profile.firstName")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.firstName && errors.firstName)}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="lastName"
                    className="fs-7"
                    label={t("profile.lastName")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.lastName && errors.lastName)}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="email"
                    type="email"
                    className="fs-7"
                    label={t("profile.email")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.email && errors.email)}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <div
                    className={`custom-phone-input ps-3 ${touched.phone && errors.phone ? "is-invalid" : ""}`}
                  >
                    <PhoneInput
                      country={country}
                      value={initialValues.phone}
                      onChange={(phone) => setFieldValue("phone", phone)}
                      inputProps={{
                        name: "phone",
                        required: true,
                        autoFocus: true,
                      }}
                      enableSearch={true}
                      specialLabel=""
                    />
                  </div>
                  <ErrorMessage
                    name="phone"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
                <div className="mb-3 mb-md-0 col-md-6">
                  <Field name="countryId">
                    {() => (
                      <ReactSelect
                        options={countriesOptions}
                        value={countriesOptions.find(
                          (country) => country.value === +values.countryId
                        )}
                        onChange={(option: SelectOption) => {
                          setFieldValue("countryId", option.value);
                        }}
                        placeholder="Select Country"
                        isLoading={countriesLading}
                        label={t("profile.country")}
                      />
                    )}
                  </Field>
                </div>
              </Row>
              <Row className="mb-4 mb-md-5">
                <p className="fw-bold fs-3">
                  {t("profile.Corporate Information")}{" "}
                  <span className="text-gray fw-normal fst-italic">
                    – {t("profile.Optional")}
                  </span>
                </p>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="companyName"
                    className="fs-7"
                    label={t("profile.Company Name")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.companyName && errors.companyName)}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="companyEmail"
                    className="fs-7"
                    label={t("profile.Company E-Mail")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.companyEmail && errors.companyEmail)}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="companyAddress"
                    className="fs-7"
                    label={t("profile.Address")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.companyAddress && errors.companyAddress)}
                    tooltip={{
                      text: t(
                        "profile.Street, Apartment (Unit), City, State (Province, Region), Zip, Country"
                      ),
                      className:
                        "tooltip-icon position-absolute pe-3 text-gray fs-5",
                      icon: <InfoCircle />,
                      placement: "bottom",
                    }}
                  />
                </div>
                <div className="form-floating mb-3 col-md-6">
                  <StyledInput
                    name="companyWebsite"
                    className="fs-7"
                    label={t("profile.Website")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.companyWebsite && errors.companyWebsite)}
                  />
                </div>
                <div className="form-floating mb-3">
                  <StyledInput
                    name="companyTaxVat"
                    className="fs-7"
                    label={t("profile.TAX VAT")}
                    labelClassName="ps-2 ms-3 z-1"
                    error={!!(touched.companyTaxVat && errors.companyTaxVat)}
                  />
                </div>
              </Row>
              <Row className="mb-4 mb-md-5">
                <p className="fw-bold fs-3">{t("profile.password")}</p>
                <div className="col-12 col-md-6">
                  <Field name="password">
                    {({ field, meta, form }: FieldProps) => (
                      <InputPassword
                        label={t("profile.newPassword")}
                        placeholder={t("profile.newPassword")}
                        id="password"
                        field={field}
                        meta={meta}
                        form={form}
                      />
                    )}
                  </Field>
                </div>
                <div className="col-12 col-md-6">
                  <Field name="passwordConfirmed">
                    {({ field, meta, form }: FieldProps) => (
                      <InputPassword
                        label={t("profile.confirm")}
                        placeholder={t("profile.confirm")}
                        id="passwordConfirmed"
                        field={field}
                        meta={meta}
                        form={form}
                      />
                    )}
                  </Field>
                </div>
              </Row>
              <div className="flex-column flex-column-reverse flex-md-row d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setShowConfirmModal(true)}
                  className="white-hover btn btn-outline-danger danger-hover text-danger col-12 col-md-4 py-3 fs-6 fw-normal"
                >
                  {t("profile.delete")}{" "}
                  {deleteLoading && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </button>
                <button
                  type="button"
                  disabled={editLoading}
                  onClick={() => handleSubmit()}
                  className="btn btn-primary text-white col-12 col-md-4 py-3 mb-3 mb-md-0 fs-6"
                >
                  {t("profile.save")}{" "}
                  {editLoading && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </button>
              </div>
            </div>
            <CustomToast
              isShow={showToast}
              setShow={setShowToast}
              message={t("profile.saved")}
            />
          </Form>
        )}
      </Formik>
      <ConfirmModal
        show={showConfirmModal}
        title={t("profile.delete")}
        description={t("profile.confirmDelete")}
        confirmText={t("profile.deleteAction")}
        onConfirm={tryDeleteAccount}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};
